/** Theme-preference helper for the „Preferințe → Temă" 3-way control on
 *  `/setari`. Reads/writes the SAME `theme` localStorage key and toggles the
 *  SAME `.dark` class on `<html>` as `ThemeToggle.tsx` (the shell's quick
 *  light↔dark flip) and `main.tsx`'s pre-paint script — do not rename the
 *  key or the class; both of those stay in sync with whatever this writes. */

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'theme';
const VALID_PREFERENCES: ReadonlySet<string> = new Set(['light', 'dark', 'system']);

/** Reads the raw `theme` value; unset or unrecognized → 'light', matching
 *  `main.tsx`'s own pre-paint fallback (so a fresh browser with nothing
 *  stored yet reflects as „Luminoasă", not „Sistem"). */
export function getThemePreference(): ThemePreference {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw && VALID_PREFERENCES.has(raw) ? (raw as ThemePreference) : 'light';
}

export function setThemePreference(pref: ThemePreference): void {
  localStorage.setItem(STORAGE_KEY, pref);
}

/** Resolves a preference to a concrete dark/light outcome — 'system' follows
 *  the OS via the caller-supplied `matchMedia('(prefers-color-scheme: dark)')`
 *  snapshot (kept as a parameter so this stays a pure function). */
export function resolveIsDark(pref: ThemePreference, prefersDarkOS: boolean): boolean {
  if (pref === 'dark') return true;
  if (pref === 'light') return false;
  return prefersDarkOS;
}

/** Applies the resolved outcome to `<html class="dark">`. */
export function applyIsDark(isDark: boolean): void {
  document.documentElement.classList.toggle('dark', isDark);
}
